import React, { PureComponent, Fragment } from 'react';
import Classnames from 'classnames';
import i18n from '../../i18n.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AnimateHeight from "react-animate-height";
import { formatPercent } from 'wt-react/dist/util/functions.js';
import BuyFund from 'wt-react/dist/components/BuyFund/BuyFund.js';
import { formatDate } from '../../common/index.js';
import FundPerformance from '../FundPerformance/FundPerformance.js';
import FundExposures from '../FundExposures/FundExposures.js';
import './FundCard.css';

// Define consts for the header options for a chart
export const FUND_PERFORMANCE = 'fund-performance';
export const FUND_DETAILS = 'fund-details';
export const EXPOSURES = 'exposures';

const CHART_HEADERS = [
    FUND_PERFORMANCE,
    EXPOSURES,
    FUND_DETAILS,
];

const EXPOSURE_TYPES = [
    'sector',
    'country',
    'assetClass',
    'marketCap',
    'creditQuality',
    'maturity',
];

class FundCard extends PureComponent {

    constructor(props) {
        super(props);

        this.state = {
            showCard: false,
            chartToShow: FUND_PERFORMANCE,
        };

        this.handleHeadingClick = this.handleHeadingClick.bind(this);
        this.handleChartClick = this.handleChartClick.bind(this);
        this.handleFundPageClick = this.handleFundPageClick.bind(this);
        
    }

    handleHeadingClick() {

        // If the model has been changed then flag it        
        this.setState({
            showCard: !this.state.showCard,
        });
    }

    handleChartClick(chartToShow) {

        // If the model has been changed then flag it        
        this.setState({
            chartToShow: chartToShow,
        });
    }

    // Handle fund page click
    handleFundPageClick() {

        const {
            fund,
        } = this.props;

        // Open the Fund Page URL
        window.open(fund.data[0].fundPageURL, '_blank');

    }

    roundToNearest5(number) {
        return number !== null && number !== undefined && number !== 0 ? `${parseFloat(Math.round(number * 100 / 5) * 5)}%` : '-';
    }

    roundToFourDp(num) {    
        return +(Math.round(num + "e+4")  + "e-4");
    }

    // Format any number as a percent
    formatPercentWithZero(number) {
        return number !== null && number !== undefined? parseFloat(number * 100).toFixed(2) + "%" : '-';
    }

    renderSecYield(){
        
        const {
            fund,
        } = this.props;

        const data = fund.data[0];
        let secYield;
        // IF the Unsubsidized and Subsidized SEC yields are the same then we can just return one
        // value. IF not then we need to show both
        if(data && data.stdPerformance && data.stdPerformance.length > 0){

            if (data.stdPerformance[0].sec30DayYieldUnSub === null ){
                secYield = null
            } else if (this.roundToFourDp(data.stdPerformance[0].sec30DayYieldUnSub) === this.roundToFourDp(data.stdPerformance[0].sec30DayYieldSub)){
                secYield = <div className='sec-yield stat'>
                    <div className='stat-value'>
                        {formatPercent(data.stdPerformance[0].sec30DayYieldUnSub)}
                    </div>
                    <div className='stat-desc'>
                        {i18n.t('sec-yield')} <br /><span className='as-of'>({i18n.t('as of')} {formatDate(data.stdPerformance[0].asOfDate)}</span>)
                    </div>
                </div>;
            } else {
               secYield = <Fragment>
                   <div className='sec-yield stat'>
                        <div className='stat-value'>
                            {formatPercent(data.stdPerformance[0].sec30DayYieldSub)}
                        </div>
                        <div className='stat-desc'>
                            {i18n.t('sec-yield-subsidized')} <br /><span className='as-of'>({i18n.t('as of')} {formatDate(data.stdPerformance[0].asOfDate)}</span>)
                        </div>
                    </div>
                    <div className='sec-yield stat'>
                        <div className='stat-value'>
                            {formatPercent(data.stdPerformance[0].sec30DayYieldUnSub)}
                        </div>
                        <div className='stat-desc'>
                            {i18n.t('sec-yield-unsubsidized')} <br /><span className='as-of'>({i18n.t('as of')} {formatDate(data.stdPerformance[0].asOfDate)}</span>)
                        </div>
                    </div>
                </Fragment>;
            }

        } 

        return secYield;

    }

    renderExpenseRatio(){
        const {
            fund,
        } = this.props;

        const data = fund.data[0];

        if (data && data.expenseRatio !== null && data.expenseRatioGross !== null){

            if (data.expenseRatio === data.expenseRatioGross){
                return  <div className='expense-ratio stat'>
                            <div className='stat-value'>
                                {formatPercent(data.expenseRatio)}
                            </div>
                            <div className='stat-desc'>
                                {i18n.t('expense-ratio')}
                            </div>
                        </div>
            } else if (data.expenseRatio !== data.expenseRatioGross){
                return  <Fragment>
                            
                            <div className='expense-ratio stat'>
                                <div className='stat-value'>
                                    {this.formatPercentWithZero(data.expenseRatioGross)}
                                </div>
                                <div className='stat-desc'>
                                    {i18n.t('gross-expense-ratio')}
                                </div>
                            </div>
                            <div className='expense-ratio stat'>
                                <div className='stat-value'>
                                    {this.formatPercentWithZero(data.expenseRatio)}
                                </div>
                                <div className='stat-desc' dangerouslySetInnerHTML={
                                        {__html: i18n.t('net-expense-ratio-charged', {interpolation: {escapeValue: false}})}
                                    }>
                                   
                                </div>
                            </div>
                        </Fragment>
            }

        } 

        return null


    }

    // Render the header and overview section of the Model Card
    renderCardOverview() {

        const {
            fund,
        } = this.props;

        const {
            showCard,
        } = this.state;

        const data = fund.data[0];

        const expenseRatio = this.renderExpenseRatio()

        const divYield = data && data.ttmDivYields ?
            <div className='div-yield stat'>
                <div className='stat-value'>
                    {formatPercent(data.ttmDivYields.ttmYieldOrdinaryIncome)}
                </div>
                <div className='stat-desc'>
                    {i18n.t('dividend-yield')} <br /><span className='as-of'>({i18n.t('as of')} {formatDate(data.ttmDivYields.date[0])}</span>)
            </div>
            </div>
            : null;

        const inceptionDate = data && data.inceptionDate ?
            <div className='inception-date stat'>
                <div className='stat-value'>
                    {formatDate(data.inceptionDate)}
                </div>
                <div className='stat-desc'>
                    {i18n.t('inception-date')}
                </div>
            </div>
            : null;

        const chevron = showCard ?
            <div className='chevron'><span className='chevron-text'>{i18n.t('close')}</span> &nbsp;
            <FontAwesomeIcon icon="chevron-down" rotation={180} size="1x" className="chevron" />
            </div>
            :
            <div className='chevron'><span className='chevron-text'>{i18n.t('details')}</span> &nbsp;
            <FontAwesomeIcon icon="chevron-down" size="1x" className="chevron" />
            </div>;

        // Show overview and Performance
        return <div
            className={Classnames('fund-card-overview', showCard ? 'selected-model' : '')}
            onClick={this.handleHeadingClick.bind(this, data)}
        >
            <div className='overview-header'>

                <div className='ticker'>
                    {data.ticker}
                </div>

                <div className='name'>
                    {fund.name}
                </div>

                <div className='fund-description'>
                    {fund.description}
                </div>

                {chevron}
            </div>

            <div className="fund-card">
                <div className='blue-frame'>
                    <div className='blue-frame-header'>
                        {divYield}
                        {this.renderSecYield()}
                        {expenseRatio}
                        {inceptionDate}
                    </div>
                </div>
            </div>
        </div>;
    }

    // Render the chart header to allow users to pick the chart they 
    // want to view
    renderChartHeader() {

        const {
            chartToShow,
        } = this.state;

        const chartHeaders = CHART_HEADERS.map((chart, index) => {
            return (
                <Fragment key={chart}>
                    <div
                        className={Classnames('chart-heading', chartToShow === chart ? 'active-heading' : '')}
                        onClick={this.handleChartClick.bind(this, chart)}
                        key={chart}
                    >
                        {i18n.t(chart)}
                    </div>

                    { index !== CHART_HEADERS.length - 1 ? <div className='chart-heading divider'>|</div> : null}
                </Fragment>
            );
        });

        return (
            <div className='chart-headings'>
                {chartHeaders}
            </div>
        );

    }

    renderExposures() {

        const {
            fund,
        } = this.props;

        const data = fund.data;

        // Render the charts based on the list of possible types
        const exposureCharts = EXPOSURE_TYPES.map(exposureType => {
            return (
                <div
                    className="model-exposures"
                    key={exposureType}>
                    <FundExposures
                        data={data[0]}
                        exposureType={exposureType}
                        sliceCategoriesToN={exposureType === 'country' ? 10 : null}
                        key={`chart-${exposureType}`}
                    />
                </div>
            );
        });

        return (
            <div className='other-cards'>
                {exposureCharts}
            </div>);
    }

    // Render the correct chart - perf, holdings, exposures etc.
    renderChart() {

        const {
            fund,
            params,
        } = this.props;

        const {
            chartToShow,
        } = this.state;

        const data = fund.data;
        const perfFootnote = fund.perfFootnote;

        let chart;

        switch (chartToShow) {

            case FUND_PERFORMANCE:
            case FUND_DETAILS:
                chart = <FundPerformance
                    data={data}
                    params={params}
                    type={chartToShow}
                    perfFootnote={perfFootnote}
                />;
                break;

            case EXPOSURES:

                chart = this.renderExposures();

                break;

            default:
                chart = null;

        }

        return chart;
    }

    renderFundDocs() {

        const {
            fund,
        } = this.props;

        // Set up the links for docs
        let docs;
        if (fund.linkNames && fund.linkUrls && fund.linkTypes) {

            docs = fund.linkNames.map((name, index) => {

                return (
                    <div
                        className='fund-document'
                        key={`${fund.ticker}_${name}`}
                    >
                        <a
                            className={Classnames(`${fund.linkTypes[index]}-link`)}
                            href={fund.linkUrls[index]}
                            target='_blank'
                            rel="noopener noreferrer"
                        >{name}
                        </a>
                    </div>
                );
            });

        }

        return <div className='fund-documents' key={fund.ticker}>
            {docs}
        </div>;

    }

    // Render the CTA buttons to launch fund detail page and Buy Now 
    renderCtas(){

        const {
            fund,
            params,
        } = this.props;

        return <div className='cta'>

            <div className='fund-page'>
                <button
                    onClick={ () => this.handleFundPageClick()}
                >
                    {i18n.t('learn-more-ticker')} {fund.ticker}
                </button>
            </div>

            {/*  Add the BuyFund component for How to Buy */}
            <div className='buy-fund-wrapper'>
                <BuyFund
                ticker={fund.ticker}
                dataspanApiUrl={params.dataspanApiUrl}
                showCompare={fund.showCompare !== null? fund.showCompare : true}
                wtRegion={params.wtRegion}
                />
            </div>
            
        </div>;
    }

    render() {

        const {
            fund,
        } = this.props;

        const {
            showCard,
        } = this.state;

        const cardOverview = this.renderCardOverview();
        const chartHeader = this.renderChartHeader();
        const chartToShow = this.renderChart();
        const fundDocs = this.renderFundDocs();
        const ctas = this.renderCtas();

        return (

            <div className="fund-card-wrapper" key={fund.ticker}>

                {/* Show the overview grey box for each model */}
                {cardOverview}

                <AnimateHeight 
                    duration={500}
                    height={showCard ? 'auto': 0}>

                    <div className="fund-card-charts">

                        <div className='fund-card-intro'>

                            {/* SHow the chart header row */}
                            {chartHeader}


                            {/* Show document links */}
                            { fundDocs }

                        </div>

                        {/* Show the chart selected */}
                        {chartToShow}

                        {/* Show the calls to action */}
                        {ctas}

                    </div>

                </AnimateHeight>

            </div>


        );
    }
}

export default (FundCard);
